import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { STATUS_ICONS } from './badge.js';

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

const TONE_CLASSES: Record<NoticeTone, string> = {
  info: 'bg-info-bg text-info',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger-bg text-danger',
};

export interface InlineNoticeProps extends React.HTMLAttributes<HTMLElement> {
  tone: NoticeTone;
  /** Defaults to the tone's status icon (non-color cue). Pass `false` to omit. */
  icon?: React.ReactNode | false;
  /** 'p' for a sentence (default); 'div' when it contains lists or blocks. */
  as?: 'p' | 'div';
  children: React.ReactNode;
}

/**
 * Tinted inline message (read-only notices, form-level errors, cautions). Pass role="alert" for errors that
 * appear after an action, role="status" for non-urgent updates, or role="note" for static guidance.
 * Text on every tint is >= 4.5:1 (see contrast.test.ts).
 */
export function InlineNotice({
  tone,
  icon,
  as: Tag = 'p',
  className,
  children,
  ...props
}: InlineNoticeProps) {
  const glyph = icon === false ? null : (icon ?? STATUS_ICONS[tone]);
  return (
    <Tag
      className={cn(
        'flex items-start gap-2 rounded-control px-3 py-2 text-body [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0',
        TONE_CLASSES[tone],
        className,
      )}
      {...props}
    >
      {glyph}
      <span className="min-w-0 flex-1">{children}</span>
    </Tag>
  );
}
